// frontend/src/components/CageManagement.js
// Mobile-First CageManagement with responsive design
// *** UPDATED to use explicit 'breeding_status' column ***

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CageFormPanel from './cages/CageFormPanel';
import { AddLitterModal, PregnancyModal } from './cages/CageBreedingModals';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';
import { DataService } from '../services/DataService';
import { useDebounce } from '../hooks/useDebounce';
import { parseSearchQuery, applySearchParams } from '../utils/searchParser';
import { SearchHelp } from './SearchHelp';
import { validateCapacity } from '../utils/validators';
import { getLocalToday, formatDisplayDate } from '../utils/dateHelpers';
import { SkeletonRow } from './SkeletonRow';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { useGeneticsOptions } from '../hooks/useGeneticsOptions';
import { useConfirm } from '../hooks/useConfirm';
import { useToast } from '../hooks/useToast';
import { ConfirmDialog } from './ConfirmDialog';
import { MAX_LIST_LIMIT } from '../utils/apiConstants';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const userColors = ['#D75B5B', '#6B8E23', '#F2A65B', '#8E7C4F', '#1F7A3D', '#1E3A3A', '#5F4B8B'];

const getUserColor = (userId) => {
    if (!userId) return '#CCCCCC'; // Default color for unknown user
    const index = userId % userColors.length;
    return userColors[index];
};

const CageManagement = ({ activeView, initialSearch, onClearInitialSearch }) => {
    const { user, token, currentViewMode } = useAuth();
    const { confirm, confirmDialogProps } = useConfirm();
    const toast = useToast();
    const [cages, setCages] = useState([]);
    const [labMembers, setLabMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    // Phase 6: action outcomes are toasts. Two inline messages survive, for the
    // two reasons a toast is the wrong shape: `loadError` explains a page that
    // is otherwise blank, and `formError` belongs beside the fields it is about
    // and must persist while the user fixes them.
    const [loadError, setLoadError] = useState('');
    const [formError, setFormError] = useState('');
    const [syncStatus, setSyncStatus] = useState('');
    const isFetchingRef = useRef(false);
    const fetchCagesRef = useRef(null);
    const fetchMiceForModalRef = useRef(null);
    const lastTapRef = useRef({});

    // CHANGED: The 'status' field is now 'breeding_status' to match the database.
    const [formData, setFormData] = useState({
        display_cage_number: '',
        location: '',
        capacity: 5,
        notes: '',
        breeding_status: 'Stock' // This now controls the Breeding/Stock status.
    });
    const [editingId, setEditingId] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTransferringCages, setIsTransferringCages] = useState(false);

    // Mobile view state
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    // State for selected cages for bulk actions
    const [selectedCagesForActions, setSelectedCagesForActions] = useState([]);
    // State for controlling the slide-out panel visibility for Add/Edit Cage
    const [showCageFormPanel, setShowCageFormPanel] = useState(false);
    // State for transfers
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [transferRecipientId, setTransferRecipientId] = useState('');

    const [showAddLitterModal, setShowAddLitterModal] = useState(false);
    const [litterModalData, setLitterModalData] = useState({
        cageId: null,
        cageDisplayNumber: '',
        initial_pup_count: '',
        date_of_birth: '',
        mother_id: '',
        father_id: ''
    });
    const [litterModalPreviousMales, setLitterModalPreviousMales] = useState([]);

    // Pregnancy state: map of cageId -> [pregnancies]
    const [cagePregnancies, setCagePregnancies] = useState({});

    // Pregnancy modal state
    const [showPregnancyModal, setShowPregnancyModal] = useState(false);
    const [pregnancyModalData, setPregnancyModalData] = useState({
        cageId: null,
        cageDisplayNumber: '',
        mother_id: '',
        father_id: '',
        breeding_start_date: getLocalToday(),
        notes: '',
    });
    const [pregnancyModalPreviousMales, setPregnancyModalPreviousMales] = useState([]);

    // Pagination states for Cages
    const [currentPageCages, setCurrentPageCages] = useState(1);
    const [itemsPerPageCages, setItemsPerPageCages] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
    const [totalCagesCount, setTotalCagesCount] = useState(0);

    // Sorting states for Cages
    const [sortColumnCages, setSortColumnCages] = useState('created_at');
    const [sortOrderCages, setSortOrderCages] = useState('desc');

    // Search states for Cages
    const [searchQueryCages, setSearchQueryCages] = useState('');
    const debouncedSearchCages = useDebounce(searchQueryCages, 350);

    const [showFiltersCages, setShowFiltersCages] = useState(false);

    // CHANGED: The filter for Breeding/Stock status is now 'filter_breeding_status'.
    const [filtersCages, setFiltersCages] = useState({
        filter_breeding_status: '', // This now controls the Breeding/Stock filter.
        cage_status: 'Active', // This controls the Active/Archived filter.
        has_mice: '',
        location: '',
        filter_genotype_locus: '',
        filter_genotype_allele: ''
    });

    const { locusOptions, alleleOptions } = useGeneticsOptions();

    // Check for mobile view on resize
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Sync on mount
    useEffect(() => {
        const performSync = async () => {
            if (navigator.onLine) {
                setSyncStatus('Syncing...');
                const result = await DataService.sync();
                setSyncStatus(result.status === 'success' ? 'Synced' : 'Sync failed');
                setTimeout(() => setSyncStatus(''), 3000);
            }
        };
        performSync();
    }, []);

    // Keep refs pointing at the latest closures.
    useEffect(() => { fetchCagesRef.current = fetchCages; });
    useEffect(() => { fetchMiceForModalRef.current = fetchMiceForModal; });

    // Re-fetch after any sync completes — silently (no skeleton flash).
    useEffect(() => {
        const onSync = () => {
            if (isFetchingRef.current) return;
            fetchCagesRef.current?.(true);
            fetchMiceForModalRef.current?.();
        };
        window.addEventListener('squeakbase-sync-complete', onSync);
        return () => window.removeEventListener('squeakbase-sync-complete', onSync);
    }, []);

    // Re-fetch when user navigates to the Cages tab (not on initial mount).
    const prevActiveViewRef = useRef('');
    useEffect(() => {
        const prev = prevActiveViewRef.current;
        prevActiveViewRef.current = activeView;
        if (prev === activeView || activeView !== 'cages') return;
        if (isFetchingRef.current) return;
        fetchCagesRef.current?.(true);
        fetchMiceForModalRef.current?.();
    }, [activeView]);

    // Apply a pre-set search filter when navigating from another view (e.g. Dashboard)
    useEffect(() => {
        if (initialSearch) {
            setSearchQueryCages(initialSearch);
            setCurrentPageCages(1);
            onClearInitialSearch?.();
        }
    }, [initialSearch]);

    // Reset filters/search when user clicks the active Cages tab again
    useEffect(() => {
        const handleTabReset = (e) => {
            if (e.detail?.view === 'cages') {
                setSearchQueryCages('');
                setFiltersCages({ filter_breeding_status: '', cage_status: 'Active', has_mice: '', location: '', filter_genotype_locus: '', filter_genotype_allele: '' });
                setShowFiltersCages(false);
                setCurrentPageCages(1);
            }
        };
        window.addEventListener('squeakbase-tab-reset', handleTabReset);
        return () => window.removeEventListener('squeakbase-tab-reset', handleTabReset);
    }, []);

    // On mobile, reset to page 1 when filters/search/sort change
    useEffect(() => {
        if (isMobile && currentPageCages !== 1) {
            setCurrentPageCages(1);
        }
    }, [isMobile, sortColumnCages, sortOrderCages, debouncedSearchCages, filtersCages]);

    useEffect(() => {
        if (isMobile && currentPageCages > 1) {
            setIsLoadingMore(true);
            fetchCages(true).finally(() => setIsLoadingMore(false));
        } else {
            fetchCages();
            fetchMiceForModal();
            fetchLabMembers();
        }
    }, [currentPageCages, itemsPerPageCages, sortColumnCages, sortOrderCages, debouncedSearchCages, filtersCages]);

    useEffect(() => {
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && showCageFormPanel) {
                resetFormAndPanel();
            }
        };

        if (showCageFormPanel) {
            document.addEventListener('keydown', handleEscapeKey);
        }

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [showCageFormPanel]);

    const fetchLabMembers = async () => {
        if (!token) return;
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/labs/members`);
            setLabMembers(response.data.members.filter(member => member.id !== user.id)); // Exclude current user
        } catch (err) {
            console.error('Failed to fetch lab members:', err);
        }
    };

    const handleTransferSelectedCages = () => {
        if (selectedCagesForActions.length !== 1) {
            toast.warn('Please select exactly one cage to transfer.');
            return;
        }
        setFormError('');
        setTransferRecipientId('');
        setShowTransferModal(true);
    };

    const handleConfirmCageTransfer = async () => {
        if (isTransferringCages) return;
        if (!transferRecipientId) {
            setFormError('Please select a recipient.');
            return;
        }
        setFormError('');

        setIsTransferringCages(true);
        try {
            const payload = {
                cage_id: selectedCagesForActions[0],
                recipient_user_id: transferRecipientId
            };

            const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/cages/transfer`, payload);
            await DataService.sync();
            toast.success(response.data.message);
            setShowTransferModal(false);
            setTransferRecipientId('');
            setSelectedCagesForActions([]);
            fetchCages();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to transfer cage.');
            console.error('Cage transfer error:', err);
        } finally {
            setIsTransferringCages(false);
        }
    };

    const fetchCages = async (silent = false) => {
        isFetchingRef.current = true;
        if (!silent) setLoading(true);
        // Silent re-fetches (sync, tab switch) need all accumulated pages to avoid duplicates
        const needsFullRefresh = silent && isMobile && currentPageCages > 1;
        const fetchPage = needsFullRefresh ? 1 : currentPageCages;
        const fetchLimit = needsFullRefresh ? currentPageCages * itemsPerPageCages : itemsPerPageCages;
        try {
            const parsedSearch = parseSearchQuery(debouncedSearchCages);
            if (!navigator.onLine) {
                // Offline: read from local cache with in-memory filtering and sorting
                let allCages = await DataService.getCagesAll();
                if (parsedSearch.freeText) {
                    const term = parsedSearch.freeText.toLowerCase();
                    allCages = allCages.filter(c =>
                        (c.display_cage_number && c.display_cage_number.toLowerCase().includes(term)) ||
                        (c.location && c.location.toLowerCase().includes(term))
                    );
                }
                // Field-specific filters from search syntax
                if (parsedSearch.fieldFilters.cage) {
                    const term = parsedSearch.fieldFilters.cage.toLowerCase();
                    allCages = allCages.filter(c =>
                        c.display_cage_number && c.display_cage_number.toLowerCase().includes(term)
                    );
                }
                if (parsedSearch.fieldFilters.mouse) {
                    const term = parsedSearch.fieldFilters.mouse.toLowerCase();
                    allCages = allCages.filter(c =>
                        (c.mice_in_cage || []).some(m => m.physical_tag && m.physical_tag.toLowerCase().includes(term))
                    );
                }
                if (parsedSearch.fieldFilters.strain) {
                    const term = parsedSearch.fieldFilters.strain.toLowerCase();
                    allCages = allCages.filter(c =>
                        (c.mice_in_cage || []).some(m => m.strain_name && m.strain_name.toLowerCase().includes(term))
                    );
                }
                if (parsedSearch.fieldFilters.location) {
                    const term = parsedSearch.fieldFilters.location.toLowerCase();
                    allCages = allCages.filter(c => c.location && c.location.toLowerCase().includes(term));
                }
                if (parsedSearch.fieldFilters.notes) {
                    const term = parsedSearch.fieldFilters.notes.toLowerCase();
                    allCages = allCages.filter(c => c.notes && c.notes.toLowerCase().includes(term));
                }
                if (parsedSearch.fieldFilters.locus) {
                    const term = parsedSearch.fieldFilters.locus.toLowerCase();
                    allCages = allCages.filter(c =>
                        (c.mice_in_cage || []).some(m =>
                            Array.isArray(m.genotype) && m.genotype.some(g => g.locus_name && g.locus_name.toLowerCase().includes(term))
                        )
                    );
                }
                if (parsedSearch.fieldFilters.allele) {
                    const term = parsedSearch.fieldFilters.allele.toLowerCase();
                    allCages = allCages.filter(c =>
                        (c.mice_in_cage || []).some(m =>
                            Array.isArray(m.genotype) && m.genotype.some(g => g.allele_name && g.allele_name.toLowerCase().includes(term))
                        )
                    );
                }
                // Status filter (Active / Archived / all)
                if (filtersCages.cage_status) {
                    allCages = allCages.filter(c => c.status === filtersCages.cage_status);
                }
                // Breeding status filter
                if (filtersCages.filter_breeding_status) {
                    allCages = allCages.filter(c => c.breeding_status === filtersCages.filter_breeding_status);
                }
                // Has mice filter
                if (filtersCages.has_mice === 'true') {
                    allCages = allCages.filter(c => (c.mouse_count || 0) > 0);
                } else if (filtersCages.has_mice === 'false') {
                    allCages = allCages.filter(c => (c.mouse_count || 0) === 0);
                }
                // Location filter
                if (filtersCages.location) {
                    allCages = allCages.filter(c => c.location === filtersCages.location);
                }
                // Genotype filters
                if (filtersCages.filter_genotype_locus || filtersCages.filter_genotype_allele) {
                    allCages = allCages.filter(c => {
                        const mice = c.mice_in_cage || [];
                        return mice.some(m => {
                            if (!Array.isArray(m.genotype)) return false;
                            const matchesLocus = !filtersCages.filter_genotype_locus ||
                                m.genotype.some(g => g.locus_name && g.locus_name.toLowerCase() === filtersCages.filter_genotype_locus.toLowerCase());
                            const matchesAllele = !filtersCages.filter_genotype_allele ||
                                m.genotype.some(g => g.allele_name && g.allele_name.toLowerCase() === filtersCages.filter_genotype_allele.toLowerCase());
                            return matchesLocus && matchesAllele;
                        });
                    });
                }
                // Sort to match server behaviour (default: created_at DESC)
                const col = sortColumnCages || 'created_at';
                const asc = sortOrderCages === 'asc';
                allCages.sort((a, b) => {
                    const valA = a[col] ?? '';
                    const valB = b[col] ?? '';
                    if (valA < valB) return asc ? -1 : 1;
                    if (valA > valB) return asc ? 1 : -1;
                    return 0;
                });
                const total = allCages.length;
                const offset = (fetchPage - 1) * fetchLimit;
                if (isMobile && currentPageCages > 1 && !needsFullRefresh) {
                    setCages(prev => [...prev, ...allCages.slice(offset, offset + fetchLimit)]);
                } else {
                    setCages(allCages.slice(offset, offset + fetchLimit));
                }
                setTotalCagesCount(total);
                setLoadError('');
                return;
            }

            const cagesParamsObj = new URLSearchParams({
                _page: fetchPage,
                _limit: fetchLimit,
                _sort: sortColumnCages,
                _order: sortOrderCages,
                ...(filtersCages.filter_breeding_status && { filter_breeding_status: filtersCages.filter_breeding_status }),
                ...(filtersCages.cage_status && { filter_cage_status: filtersCages.cage_status }),
                ...(filtersCages.has_mice && { filter_has_mice: filtersCages.has_mice }),
                ...(filtersCages.location && { filter_location: filtersCages.location }),
                ...(filtersCages.filter_genotype_locus && { filter_genotype_locus: filtersCages.filter_genotype_locus }),
                ...(filtersCages.filter_genotype_allele && { filter_genotype_allele: filtersCages.filter_genotype_allele })
            });
            applySearchParams(cagesParamsObj, parsedSearch);
            const cagesParams = cagesParamsObj.toString();

            const cagesResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/cages?${cagesParams}`);
            if (isMobile && currentPageCages > 1 && !needsFullRefresh) {
                setCages(prev => [...prev, ...cagesResponse.data.data]);
            } else {
                setCages(cagesResponse.data.data);
            }
            setTotalCagesCount(cagesResponse.data.totalCount);
            setLoadError('');
        } catch (err) {
            // If the error is a 401, the useAuth hook will handle the logout
            if (err.response && err.response.status !== 401)
                setLoadError('Failed to load data');
            console.error('Fetch error:', err);
        } finally {
            isFetchingRef.current = false;
            setLoading(false);
        }
    };

    // Fetch pregnancies for breeding cages whenever cage list changes
    useEffect(() => {
        const breedingIds = cages.filter(c => c.breeding_status === 'Breeding').map(c => c.id);
        if (breedingIds.length > 0 && navigator.onLine) {
            fetchCagePregnancies(breedingIds);
        }
    }, [cages]);

    const [mice, setMice] = useState([]);
    const fetchMiceForModal = async () => {
        try {
            // Try cache first
            const cached = await DataService.getMice({ status: 'Active', limit: 1000000 });
            if (cached.data.length > 0) {
                setMice(cached.data);
                return;
            }
            if (!navigator.onLine) return;
            const miceResponse = await axios.get(`${process.env.REACT_APP_API_URL}/api/mice?_limit=${MAX_LIST_LIMIT}&filter_status=Active`);
            setMice(miceResponse.data.data);
        } catch (err) {
            console.error('Failed to fetch mice for modal:', err);
        }
    };

    const handleDoubleClick = (cage) => {
        handleEdit(cage);
    };

    const formatMouseForDropdown = (mouse) => {
        const cage = mouse.display_cage_number || 'N/A';
        const tag = mouse.physical_tag || 'No Tag';
        const genotype = formatGenotype(mouse.genotype);
        const dob = formatDisplayDate(mouse.date_of_birth);
        return `${cage}; ${tag}; ${genotype}; ${dob}`;
    };

    const resetFormAndPanel = () => {
        // CHANGED: Reset 'breeding_status' in the form data.
        setFormData({
            display_cage_number: '',
            location: '',
            capacity: 5,
            notes: '',
            breeding_status: 'Stock'
        });
        setEditingId(null);
        setShowCageFormPanel(false);
        setSelectedCagesForActions([]);
        setFormError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        if (!formData.display_cage_number || !formData.display_cage_number.trim()) {
            setFormError('Please enter a display cage name.');
            return;
        }

        // C14: capacity had no client-side check at all, so a 0 or a stray
        // minus sign only failed at the server, as a 500 with nothing the user
        // could act on.
        const capacityError = validateCapacity(formData.capacity);
        if (capacityError) {
            setFormError(capacityError);
            return;
        }

        setIsSubmitting(true);
        setFormError('');

        try {
            if (editingId) {
                if (Array.isArray(editingId)) {
                    // Bulk update — complex server-side endpoint, keep online-only then refresh
                    const updates = {};
                    if (formData.location) updates.location = formData.location;
                    if (formData.capacity) updates.capacity = formData.capacity;
                    if (formData.notes) updates.notes = formData.notes;
                    if (formData.breeding_status) updates.status = formData.breeding_status;

                    const payload = { cage_ids: editingId, updates };
                    await axios.put(`${process.env.REACT_APP_API_URL}/api/cages/bulk-update`, payload);
                    await DataService.sync();
                    toast.success(`${editingId.length} cages updated successfully!`);
                    resetFormAndPanel();
                    fetchCages();
                } else {
                    // Single update — optimistic update in state
                    const savedCage = await DataService.saveCage({
                        id: editingId,
                        display_cage_number: formData.display_cage_number,
                        location: formData.location,
                        capacity: formData.capacity,
                        notes: formData.notes,
                        breeding_status: formData.breeding_status,
                    });
                    setCages(prev => prev.map(c => c.id === editingId ? { ...c, ...savedCage } : c));
                    toast.success('Cage updated successfully!');
                    resetFormAndPanel();
                }
            } else {
                // Create new cage — add to top of list immediately
                const savedCage = await DataService.saveCage({
                    display_cage_number: formData.display_cage_number,
                    location: formData.location,
                    capacity: formData.capacity,
                    notes: formData.notes,
                    breeding_status: formData.breeding_status,
                });
                setCages(prev => [savedCage, ...prev]);
                toast.success('Cage created successfully!');
                resetFormAndPanel();
            }
        } catch (err) {
            fetchCages(); // rollback on error
            toast.error(err.response?.data?.error || 'Failed to save cage');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (cage) => {
        // CHANGED: Populate the form with 'breeding_status' from the cage object.
        setFormData({
            display_cage_number: cage.display_cage_number,
            location: cage.location || '',
            capacity: cage.capacity,
            notes: cage.notes || '',
            breeding_status: cage.breeding_status // Use the new explicit field
        });
        setEditingId(cage.id);
        setShowCageFormPanel(true);
        setFormError('');
    };

    const handleDelete = async (id, cageDisplayNumber, mouseCount) => {
        let confirmationMessage = mouseCount > 0
            ? `Archive cage ${cageDisplayNumber}? It holds ${mouseCount} active mouse(s), which will be archived with it. You can restore the cage from the Archived Cages view; its mice stay archived until you restore them from the Archived Mice view.`
            : `Archive cage ${cageDisplayNumber}? You can restore it later from the Archived Cages view.`;

        if (!await confirm(confirmationMessage, {
            title: 'Confirm Archive',
            confirmLabel: 'Yes, Archive',
            danger: true
        })) return;

        setFormError('');
        try {
            setCages(prev => prev.filter(c => c.id !== id));
            await DataService.archiveCage(id);
            toast.success('Cage archived successfully!');
        } catch (err) {
            fetchCages(); // rollback on error
            toast.error(err.response?.data?.error || 'Failed to archive cage.');
            console.error('Archive cage error:', err);
        }
    };

    const handleRestoreCage = async (id, cageDisplayNumber) => {
        if (!await confirm(`Are you sure you want to restore cage ${cageDisplayNumber}?`, {
            title: 'Confirm Restore',
            confirmLabel: 'Yes, Restore'
        })) return;

        setFormError('');
        resetFormAndPanel();

        try {
            const response = await axios.put(`${process.env.REACT_APP_API_URL}/api/cages/${id}/restore`, {});
            await DataService.sync();
            toast.success(response.data.message || 'Cage restored successfully!');
            fetchCages();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to restore cage.');
            console.error('Restore cage error:', err);
        }
    };

    const handleRestoreSelectedCages = async () => {
        if (selectedCagesForActions.length === 0) {
            toast.warn('Please select at least one cage to restore.');
            return;
        }

        if (!await confirm(`Are you sure you want to restore ${selectedCagesForActions.length} selected cage(s)?`, {
            title: 'Confirm Restore',
            confirmLabel: 'Yes, Restore'
        })) return;

        try {
            const restorePromises = selectedCagesForActions.map(cageId =>
                axios.put(`${process.env.REACT_APP_API_URL}/api/cages/${cageId}/restore`, {})
            );
            await Promise.all(restorePromises);
            await DataService.sync();
            toast.success(`${selectedCagesForActions.length} cage(s) restored successfully!`);
            setSelectedCagesForActions([]);
            fetchCages();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to restore selected cages.');
            console.error('Bulk restore error:', err);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: name === 'capacity' ? parseInt(value) : value
        });
    };

    const formatGenotype = (genotypeArray) => {
        if (!genotypeArray || genotypeArray.length === 0) {
            return 'WT';
        }
        return genotypeArray.map(g => `${g.locus_name}(${g.allele_name})`).join(', ');
    };


    const handleAddLitterClick = async (cageId, cageDisplayNumber) => {
        const today = getLocalToday();
        const miceInSelectedCage = mice.filter(mouse => String(mouse.cage_id) === String(cageId) && mouse.status === 'Active');
        const motherCandidate = miceInSelectedCage.find(mouse => mouse.sex === 'Female');
        const fatherCandidate = miceInSelectedCage.find(mouse => mouse.sex === 'Male');

        setLitterModalPreviousMales([]);
        setLitterModalData({
            cageId: cageId,
            cageDisplayNumber: cageDisplayNumber,
            initial_pup_count: '',
            date_of_birth: today,
            mother_id: motherCandidate ? motherCandidate.id : '',
            father_id: fatherCandidate ? fatherCandidate.id : ''
        });
        setShowAddLitterModal(true);
        setFormError('');

        if (navigator.onLine) {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/cages/${cageId}/previous-males`);
                setLitterModalPreviousMales(response.data);
            } catch (err) {
                console.error('Failed to fetch previous males:', err);
            }
        }
    };

    const calculateAgeInWeeks = (dobString) => {
        if (!dobString) return 'N/A';
        const dob = new Date(dobString);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - dob.getTime());
        const diffWeeks = (diffTime / (1000 * 60 * 60 * 24 * 7)).toFixed(1);
        return `${diffWeeks} wks`;
    };


    const handleConfirmAddLitter = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setFormError('');

        if (!litterModalData.mother_id || !litterModalData.father_id) {
            setFormError("Please select both a mother and a father for the litter");
            return;
        }

        setIsSubmitting(true);
        try {
            const litterPayload = {
                date_of_birth: litterModalData.date_of_birth,
                mother_id: litterModalData.mother_id,
                father_id: litterModalData.father_id,
                cage_id: litterModalData.cageId,
                initial_pup_count: parseInt(litterModalData.initial_pup_count, 10) || 1,
            };

            await DataService.saveLitter(litterPayload);

            const mother = mice.find(m => m.id === litterModalData.mother_id);
            const father = mice.find(m => m.id === litterModalData.father_id);
            const motherTag = mother ? (mother.physical_tag || `ID: ${mother.mouse_id}`) : 'N/A';
            const fatherTag = father ? (father.physical_tag || `ID: ${father.mouse_id}`) : 'N/A';

            toast.success(`Litter added to Cage ${litterModalData.cageDisplayNumber}! Parents set to ${motherTag} (Mother) and ${fatherTag} (Father).`);

            setShowAddLitterModal(false);
            setLitterModalData({ cageId: null, cageDisplayNumber: '', initial_pup_count: '', date_of_birth: '', mother_id: '', father_id: '' });
            fetchCages();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to add litter.');
            console.error('Add litter error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Pregnancy handlers
    const handleAddPregnancyClick = async (cageId, cageDisplayNumber) => {
        const cageMice = mice.filter(m => String(m.cage_id) === String(cageId) && m.status === 'Active');
        const motherCandidate = cageMice.find(m => m.sex === 'Female');
        const fatherCandidate = cageMice.find(m => m.sex === 'Male');

        setPregnancyModalPreviousMales([]);
        setPregnancyModalData({
            cageId,
            cageDisplayNumber,
            mother_id: motherCandidate ? motherCandidate.id : '',
            father_id: fatherCandidate ? fatherCandidate.id : '',
            breeding_start_date: getLocalToday(),
            notes: '',
        });
        setShowPregnancyModal(true);
        setFormError('');

        if (navigator.onLine) {
            try {
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/cages/${cageId}/previous-males`);
                setPregnancyModalPreviousMales(response.data);
            } catch (err) {
                console.error('Failed to fetch previous males:', err);
            }
        }
    };

    const handleConfirmAddPregnancy = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setFormError('');

        if (!pregnancyModalData.mother_id || !pregnancyModalData.father_id) {
            setFormError("Please select both a mother and a father");
            return;
        }

        setIsSubmitting(true);
        try {
            await DataService.createPregnancy({
                mother_id: pregnancyModalData.mother_id,
                father_id: pregnancyModalData.father_id,
                cage_id: pregnancyModalData.cageId,
                breeding_start_date: pregnancyModalData.breeding_start_date,
                notes: pregnancyModalData.notes
            });
            toast.success(`Pregnancy recorded for Cage ${pregnancyModalData.cageDisplayNumber}`);
            fetchCagePregnancies([pregnancyModalData.cageId]);
            setShowPregnancyModal(false);
            setPregnancyModalData({
                cageId: null,
                cageDisplayNumber: '',
                mother_id: '',
                father_id: '',
                breeding_start_date: getLocalToday(),
                notes: '',
            });
            setPregnancyModalPreviousMales([]);
            fetchCages();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to create pregnancy');
            console.error('Add pregnancy error:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Fetch active pregnancies for all breeding cages
    const fetchCagePregnancies = async (breedingCageIds) => {
        if (!breedingCageIds || breedingCageIds.length === 0) return;
        try {
            const results = {};
            await Promise.all(breedingCageIds.map(async (cageId) => {
                const data = await DataService.getPregnanciesByCage(cageId);
                const pregnancies = Array.isArray(data) ? data : data?.data || [];
                if (pregnancies.length > 0) {
                    results[cageId] = pregnancies;
                }
            }));
            setCagePregnancies(prev => ({ ...prev, ...results }));
        } catch (err) {
            console.error('Failed to fetch cage pregnancies:', err);
        }
    };

    // Update pregnancy confirmation status
    const handleUpdatePregnancy = async (pregnancyId, cageId, updateData) => {
        try {
            await DataService.updatePregnancy(pregnancyId, updateData);
            // Refresh pregnancies for this cage
            const data = await DataService.getPregnanciesByCage(cageId);
            const pregnancies = Array.isArray(data) ? data : data?.data || [];
            setCagePregnancies(prev => ({
                ...prev,
                [cageId]: pregnancies.length > 0 ? pregnancies : undefined
            }));
            toast.success('Pregnancy updated');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to update pregnancy');
        }
    };

    const handleSortCages = (column) => {
        if (sortColumnCages === column) {
            setSortOrderCages(sortOrderCages === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumnCages(column);
            setSortOrderCages('asc');
        }
        setCurrentPageCages(1);
    };

    const handlePageChangeCages = (pageNumber) => {
        setCurrentPageCages(pageNumber);
    };

    const handleItemsPerPageChangeCages = (e) => {
        setItemsPerPageCages(parseInt(e.target.value, 10));
        setCurrentPageCages(1);
    };

    const totalPagesCages = Math.ceil(totalCagesCount / itemsPerPageCages);

    const handleLoadMoreCages = useCallback(() => {
        if (currentPageCages < totalPagesCages) {
            setCurrentPageCages(prev => prev + 1);
        }
    }, [currentPageCages, totalPagesCages]);

    useInfiniteScroll({
        enabled: isMobile && activeView === 'cages',
        hasMore: currentPageCages < totalPagesCages,
        loading: loading || isLoadingMore,
        onLoadMore: handleLoadMoreCages,
    });

    const handleSearchCages = (e) => {
        setSearchQueryCages(e.target.value);
        setCurrentPageCages(1);
    };

    const clearSearchCages = () => {
        setSearchQueryCages('');
        setCurrentPageCages(1);
    };

    const handleFilterChangeCages = (filterType, value) => {
        setFiltersCages(prev => ({
            ...prev,
            [filterType]: value
        }));
        setCurrentPageCages(1);
    };

    const clearFiltersCages = () => {
        // CHANGED: Reset 'filter_breeding_status' instead of 'status'.
        setFiltersCages({
            filter_breeding_status: '',
            cage_status: 'Active',
            has_mice: '',
            location: '',
            filter_genotype_locus: '',
            filter_genotype_allele: ''
        });
        setCurrentPageCages(1);
    };

    const hasActiveFiltersCages = Object.values(filtersCages).some(filter => filter !== '' && filter !== 'Active');

    const uniqueLocations = [...new Set(cages.map(cage => cage.location).filter(Boolean))];

    const handleCageCardSelect = (cageId) => {
        setSelectedCagesForActions(prevSelected => {
            const isSelected = prevSelected.includes(cageId);
            if (isSelected) {
                return prevSelected.filter(id => id !== cageId);
            } else {
                return [...prevSelected, cageId];
            }
        });
    };

    const handleEditSelectedCages = () => {
        if (selectedCagesForActions.length === 0) {
            toast.warn('Please select at least one cage to edit.');
            return;
        }

        const selectedCagesData = cages.filter(cage => selectedCagesForActions.includes(cage.id));

        if (selectedCagesForActions.length === 1) {
            handleEdit(selectedCagesData[0]);
        } else {
            // Handle multiple cages editing
            const allSame = (prop) => selectedCagesData.every(cage => cage[prop] === selectedCagesData[0][prop]);

            setFormData({
                display_cage_number: 'Multiple Values',
                location: allSame('location') ? selectedCagesData[0].location : '',
                capacity: allSame('capacity') ? selectedCagesData[0].capacity : '',
                notes: allSame('notes') ? selectedCagesData[0].notes : '',
                breeding_status: allSame('breeding_status') ? selectedCagesData[0].breeding_status : 'Stock',
            });

            setEditingId(selectedCagesForActions); // Store array of IDs
            setShowCageFormPanel(true);
            setFormError('');
        }
    };

    const handleDeleteSelectedCages = async () => {
        if (selectedCagesForActions.length === 0) {
            toast.warn('Please select at least one cage to archive.');
            return;
        }

        const cagesWithMice = cages.filter(cage =>
            selectedCagesForActions.includes(cage.id) && parseInt(cage.mouse_count) > 0
        );

        let confirmationMessage = cagesWithMice.length > 0
            ? `Archive ${selectedCagesForActions.length} selected cage(s)? Some (${cagesWithMice.map(c => c.display_cage_number).join(', ')}) hold active mice, which will be archived with them. You can restore the cages from the Archived Cages view; their mice stay archived until you restore them from the Archived Mice view.`
            : `Archive ${selectedCagesForActions.length} selected cage(s)? You can restore them later from the Archived Cages view.`;

        if (!await confirm(confirmationMessage, {
            title: 'Confirm Archive',
            confirmLabel: 'Yes, Archive',
            danger: true
        })) return;

        setFormError('');
        resetFormAndPanel();

        try {
            const idsToRemove = [...selectedCagesForActions];
            setCages(prev => prev.filter(c => !idsToRemove.includes(c.id)));
            await Promise.all(idsToRemove.map(id => DataService.archiveCage(id)));
            toast.success(`${idsToRemove.length} cage(s) archived successfully!`);
            setSelectedCagesForActions([]);
        } catch (err) {
            fetchCages(); // rollback on error
            toast.error(err.response?.data?.error || 'Failed to archive selected cages.');
            console.error('Bulk archive error:', err);
        }
    };

    const handleDoubleTap = (id, item, callback) => {
        const now = Date.now();
        if (lastTapRef.current.id === id && now - lastTapRef.current.time < 300) {
            callback(item);
            lastTapRef.current = {};
        } else {
            lastTapRef.current = { id, time: now };
        }
    };

    // Get the active pregnancy for a cage (if any)
    const getActivePregnancy = (cageId) => {
        const pregnancies = cagePregnancies[cageId];
        if (!pregnancies || pregnancies.length === 0) return null;
        return pregnancies[0];
    };

    // Render pregnancy status inline: "Due in Xd" label + cancel button
    // context: 'mobile' uses action-btn (rounded pill), 'desktop' uses primary-button (8px radius)
    const renderPregnancyInline = (cageId, context = 'mobile') => {
        const p = getActivePregnancy(cageId);
        if (!p) return null;
        const daysUntil = p.days_until_due;
        const urgencyColor = daysUntil <= 3 ? '#dc3545' : daysUntil <= 7 ? '#FF9800' : '#6f42c1';
        const btnClass = context === 'mobile' ? 'action-btn' : 'primary-button';
        return (
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: '4px' }} onClick={e => e.stopPropagation()}>
                <button
                    disabled
                    className={btnClass}
                    style={{ flex: 1, fontSize: '0.78rem', fontWeight: 600, backgroundColor: urgencyColor, color: 'white', opacity: 1, cursor: 'default', border: 'none' }}
                >
                    {daysUntil <= 0 ? 'Due today!' : `Due in ${daysUntil}d`}
                </button>
                <button
                    onClick={() => handleUpdatePregnancy(p.id, cageId, { status: 'Cancelled' })}
                    title="Cancel pregnancy"
                    className={btnClass}
                    style={{ backgroundColor: '#dc3545', color: 'white', fontSize: '0.78rem', width: 'auto', border: 'none' }}
                >
                    ✕
                </button>
            </div>
        );
    };

    const renderMobileCageList = () => {
        return (
            <div className="mobile-list">
                {cages.map((cage) => {
                    return (
                        <div key={cage.id} style={{ borderBottom: '1px solid #f1f3f5' }}>
                            <div className="list-item"
                                onClick={() => handleDoubleTap(cage.id, cage, handleEdit)}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: selectedCagesForActions.includes(cage.id) ? 'rgba(0,123,255,0.1)' : 'transparent',
                                    borderBottom: 'none'
                                }}
                            >
                                <div className="item-content">
                                    <div className="item-title">
                                        {cage.display_cage_number}
                                    </div>
                                    <div className="item-subtitle">
                                        {cage.location || 'No location'}
                                    </div>
                                    <div className="item-meta">
                                        <span className={`status-${cage.breeding_status?.toLowerCase()}`} style={{ marginRight: '8px' }}>
                                            <span className="status-indicator status-breeding"></span>
                                            {cage.breeding_status}
                                        </span>
                                        • {Array.isArray(cage.mice_in_cage) ? cage.mice_in_cage.length : (Number(cage.mouse_count) || 0)}/{cage.capacity}
                                    </div>
                                    {cage.notes && (
                                        <div className="item-notes" style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#666' }}>
                                            Notes: {cage.notes}
                                        </div>
                                    )}
                                    {cage.mice_in_cage && cage.mice_in_cage.length > 0 && (
                                        <div style={{ marginTop: '0.5rem' }}>
                                            {cage.mice_in_cage.map(mouse => (
                                                <p key={mouse.id} className="genotype-chip">
                                                    {mouse.sex}, {mouse.physical_tag ? mouse.physical_tag + ', ' : ''}{formatGenotype(mouse.genotype)} ({calculateAgeInWeeks(mouse.date_of_birth)})
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="item-actions column-actions" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        className="action-btn primary"
                                        onClick={() => handleEdit(cage)}
                                    >
                                        Edit
                                    </button>
                                    {filtersCages.cage_status === 'Active' ? (
                                        <button
                                            className="action-btn"
                                            onClick={() => handleDelete(cage.id, cage.display_cage_number, cage.mouse_count)}
                                            style={{ backgroundColor: '#dc3545', color: 'white' }}
                                        >
                                            Archive
                                        </button>) : (
                                        <button
                                            className="action-btn"
                                            onClick={() => handleRestoreCage(cage.id, cage.display_cage_number)}
                                            style={{ backgroundColor: '#28a745', color: 'white' }}
                                        >
                                            Restore
                                        </button>)}
                                    {cage.breeding_status === 'Breeding' && (
                                        <>
                                            <button
                                                className="action-btn"
                                                onClick={() => handleAddLitterClick(cage.id, cage.display_cage_number)}
                                                style={{ backgroundColor: '#28a745', color: 'white' }}
                                            >
                                                Litter
                                            </button>
                                            {!getActivePregnancy(cage.id) && (
                                                <button
                                                    className="action-btn"
                                                    onClick={() => handleAddPregnancyClick(cage.id, cage.display_cage_number)}
                                                    style={{ backgroundColor: '#6f42c1', color: 'white' }}
                                                >
                                                    Pregnancy
                                                </button>
                                            )}
                                        </>
                                    )}
                                    {renderPregnancyInline(cage.id)}
                                </div>
                            </div>
                            {cage.breeding_status === 'Breeding' && (
                                <div style={{ padding: '0 1rem 0.5rem', fontSize: '13px' }}>
                                    <div className="item-meta" style={{ marginTop: '4px' }}>
                                        {cage.days_since_last_litter !== null ? (
                                            <span style={{ color: cage.days_since_last_litter > 60 ? 'red' : 'inherit' }}>
                                                Last litter: {cage.days_since_last_litter} day(s) ago
                                            </span>
                                        ) : (
                                            <span>No litters recorded</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderDesktopCageTable = () => {
        return (
            <table className="cage-table">
                <thead>
                    <tr>
                        <th style={{ width: '30px' }}>
                            <input
                                type="checkbox"
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedCagesForActions(cages.map(cage => cage.id));
                                    } else {
                                        setSelectedCagesForActions([]);
                                    }
                                }}
                                checked={selectedCagesForActions.length === cages.length && cages.length > 0}
                                ref={input => {
                                    if (input) {
                                        input.indeterminate = selectedCagesForActions.length > 0 && selectedCagesForActions.length < cages.length;
                                    }
                                }}
                            />
                        </th>

                        <th onClick={() => handleSortCages('display_cage_number')}>
                            Cage {sortColumnCages === 'display_cage_number' ? (sortOrderCages === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th onClick={() => handleSortCages('location')}>
                            Location {sortColumnCages === 'location' ? (sortOrderCages === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th>Mice in Cage</th>
                        <th onClick={() => handleSortCages('notes')}>
                            Notes {sortColumnCages === 'notes' ? (sortOrderCages === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        {/* CHANGED: Sort by 'breeding_status' */}
                        <th onClick={() => handleSortCages('breeding_status')}>
                            Status {sortColumnCages === 'breeding_status' ? (sortOrderCages === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th>Actions</th>
                        <th>Owner</th>
                    </tr>
                </thead>
                <tbody>
                    {cages.map((cage) => {
                        const miceInCage = cage.mice_in_cage || [];
                        return (
                            <tr key={cage.id}
                                onClick={() => handleCageCardSelect(cage.id)}
                                onDoubleClick={() => handleDoubleClick(cage)}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: selectedCagesForActions.includes(cage.id) ? 'rgba(0,123,255,0.1)' : 'transparent'
                                }}>
                                <td onClick={(e) => e.stopPropagation()}>
                                    <input
                                        type="checkbox"
                                        checked={selectedCagesForActions.includes(cage.id)}
                                        onChange={() => handleCageCardSelect(cage.id)}
                                    />
                                </td>
                                <td>{cage.display_cage_number}</td>
                                <td>{cage.location || 'Not specified'}</td>
                                <td>
                                    {miceInCage.length > 0 ? (
                                        <ul className="genotype-list">
                                            {miceInCage.map(mouse => (
                                                <li key={mouse.id}>
                                                    <span>{mouse.physical_tag ? mouse.physical_tag + ', ' : ''}</span>
                                                    <span>{mouse.sex}, </span>
                                                    <span>{formatGenotype(mouse.genotype)} </span>
                                                    <span className="dob-text">({calculateAgeInWeeks(mouse.date_of_birth)})</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        'No mice'
                                    )}
                                </td>
                                <td style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {cage.notes || ''}</td>
                                {/* CHANGED: Use cage.breeding_status for display and styling */}
                                <td>
                                    <span className={cage.breeding_status === 'Breeding' ? 'status-breeding' : 'status-stock'}>
                                        {cage.breeding_status}
                                    </span>
                                </td>
                                <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                                    <div style={{ display: 'flex', gap: '5px', flexDirection: 'column' }}>
                                        {/* CHANGED: Use cage.breeding_status for conditional rendering */}
                                        {cage.breeding_status === 'Breeding' && (
                                            <>
                                                <button
                                                    onClick={() => handleAddLitterClick(cage.id, cage.display_cage_number)}
                                                    style={{ flex: 1, padding: '5px 10px', fontSize: '14px', backgroundColor: '#28a745' }}
                                                    className="primary-button"
                                                >
                                                    Litter
                                                </button>
                                                {!getActivePregnancy(cage.id) && (
                                                    <button
                                                        onClick={() => handleAddPregnancyClick(cage.id, cage.display_cage_number)}
                                                        style={{ flex: 1, padding: '5px 10px', fontSize: '14px', backgroundColor: '#6f42c1' }}
                                                        className="primary-button"
                                                    >
                                                        Pregnancy
                                                    </button>
                                                )}
                                            </>
                                        )}
                                        {renderPregnancyInline(cage.id, 'desktop')}
                                        {cage.breeding_status === 'Breeding' && (
                                            <span style={{ color: cage.days_since_last_litter > 60 ? 'red' : 'inherit' }}>
                                                {cage.days_since_last_litter !== null ? `${cage.days_since_last_litter} days since last litter` : `${cage.days_since_creation} days since breeding`}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td style={{ justifyContent: 'center', alignItems: 'center' }}>
                                    <div
                                        style={{
                                            width: '30px',
                                            height: '30px',
                                            borderRadius: '8px',
                                            backgroundColor: getUserColor(cage.user_id),
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: 'bold',
                                        }}
                                        title={cage.owner_initials}
                                    >{cage.owner_initials}</div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    };

    const activeFemales = mice.filter(m => m.sex === 'Female' && m.status === 'Active');
    const activeMales = mice.filter(m => m.sex === 'Male' && m.status === 'Active');

    return (
        <div className="cage-management">
            {confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />}
            <div className="main-content cage-management-grid">
                {syncStatus && (
                    <div style={{ padding: '5px 10px', backgroundColor: syncStatus === 'Sync failed' ? '#fff3cd' : '#eef', textAlign: 'center', fontSize: '0.8rem', color: syncStatus === 'Sync failed' ? '#856404' : '#558', borderBottom: '1px solid #dde' }}>
                        {syncStatus}
                    </div>
                )}
                <div className="mice-section">
                    <div className="sticky-toolbar">
                        <div className="search-bar">
                            <input
                                type="text"
                                placeholder="Search... (try cage:Blue-42)"
                                value={searchQueryCages}
                                onChange={handleSearchCages}
                            />
                            {/* No sex: — routes/cages.js reads every other
                                search_* param but not search_sex, and listing a
                                prefix that silently does nothing is worse than
                                omitting it. */}
                            <SearchHelp fields={['cage', 'mouse', 'strain', 'location', 'locus', 'allele', 'notes']} />
                            {searchQueryCages && (
                                <button onClick={clearSearchCages} style={{ background: 'none', border: 'none', fontSize: '16px', cursor: 'pointer', color: '#666', width: 'auto' }} title="Clear search" >
                                    ✕
                                </button>
                            )}
                            <button
                                className={`filter-toggle-btn${hasActiveFiltersCages ? ' filter-toggle-btn--active' : ''}`}
                                onClick={() => setShowFiltersCages(v => !v)}
                                title="Toggle filters"
                            >
                                <span className="filter-icon"><span /><span /><span /></span>
                                {hasActiveFiltersCages && <span className="filter-active-dot" />}
                            </button>
                        </div>

                        {/* CHANGED: The filter dropdown now controls 'filter_breeding_status' */}
                        {showFiltersCages && (
                            <div className="filter-controls">
                                <select
                                    value={filtersCages.filter_breeding_status}
                                    onChange={(e) => handleFilterChangeCages('filter_breeding_status', e.target.value)}
                                >
                                    <option value="">All Breeding Statuses</option>
                                    <option value="Breeding">Breeding</option>
                                    <option value="Stock">Stock</option>
                                </select>

                                <select
                                    value={filtersCages.cage_status}
                                    onChange={(e) => handleFilterChangeCages('cage_status', e.target.value)}
                                >
                                    <option value="Active">Active Cages</option>
                                    <option value="Archived">Archived Cages</option>
                                    <option value="">All Cages (Active & Archived)</option>
                                </select>

                                <select
                                    value={filtersCages.has_mice}
                                    onChange={(e) => handleFilterChangeCages('has_mice', e.target.value)}
                                >
                                    <option value="">All Cages</option>
                                    <option value="true">With Mice</option>
                                    <option value="false">Empty Cages</option>
                                </select>

                                {uniqueLocations.length > 0 && (
                                    <select
                                        value={filtersCages.location}
                                        onChange={(e) => handleFilterChangeCages('location', e.target.value)}
                                    >
                                        <option value="">All Locations</option>
                                        {uniqueLocations.map(location => (
                                            <option key={location} value={location}>{location}</option>
                                        ))}
                                    </select>
                                )}

                                <select value={filtersCages.filter_genotype_locus} onChange={(e) => handleFilterChangeCages('filter_genotype_locus', e.target.value)}>
                                    <option value="">Filter by Locus (Mice in Cage)</option>
                                    {locusOptions.map(locus => (<option key={locus.id} value={locus.locus_name}>{locus.locus_name}</option>))}
                                </select>
                                <select value={filtersCages.filter_genotype_allele} onChange={(e) => handleFilterChangeCages('filter_genotype_allele', e.target.value)}>
                                    <option value="">Filter by Allele (Mice in Cage)</option>
                                    {alleleOptions.map(allele => (<option key={allele.id} value={allele.allele_name}>{allele.allele_name}</option>))}
                                </select>

                                {hasActiveFiltersCages && (
                                    <button onClick={clearFiltersCages} style={{ background: '#dc3545', color: 'white', border: 'none', borderRadius: '20px', padding: '0.5rem 1rem', fontSize: '0.9rem', cursor: 'pointer' }} title="Clear all filters">
                                        Clear Filters
                                    </button>
                                )}
                            </div>
                        )}

                        {!isMobile && (
                            <div className="action-buttons">
                                <button onClick={() => { setEditingId(null); setFormData({ display_cage_number: '', location: '', capacity: 5, notes: '', breeding_status: 'Stock' }); setShowCageFormPanel(true); setFormError(''); }} className="primary-button fab">
                                    Add New Cage
                                </button>
                                <button onClick={handleEditSelectedCages} className="primary-button fab" disabled={selectedCagesForActions.length === 0}>
                                    Edit ({selectedCagesForActions.length})
                                </button>
                                {user.lab_id && labMembers.length > 0 && (
                                    <button
                                        onClick={handleTransferSelectedCages}
                                        className="primary-button fab"
                                        style={{ backgroundColor: '#17a2b8' }}
                                        disabled={selectedCagesForActions.length !== 1}
                                    >
                                        Transfer Cage
                                    </button>
                                )}
                                <button onClick={handleDeleteSelectedCages} className="secondary-button fab" style={{ backgroundColor: '#dc3545' }} disabled={selectedCagesForActions.length === 0}>
                                    Archive ({selectedCagesForActions.length})
                                </button>
                                {filtersCages.cage_status === 'Archived' && (
                                    <button
                                        onClick={handleRestoreSelectedCages}
                                        className="primary-button fab"
                                        style={{ backgroundColor: '#28a745' }}
                                        disabled={selectedCagesForActions.length === 0}>
                                        Restore ({selectedCagesForActions.length})
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {isMobile && (
                        <button className="fab" onClick={() => { setEditingId(null); setFormData({ display_cage_number: '', location: '', capacity: 5, notes: '', breeding_status: 'Stock' }); setShowCageFormPanel(true); setFormError(''); }} title="Add New Cage">
                            +
                        </button>
                    )}

                    {loadError && <div className="error">{loadError}</div>}

                    {loading ? (
                        <SkeletonRow count={itemsPerPageCages > 10 ? 10 : itemsPerPageCages} />
                    ) : cages.length === 0 && totalCagesCount === 0 ? (
                        <div className="empty-state">
                            <p>No cages found.</p>
                            <p>Create your first cage using the + button, or adjust your filters.</p>
                        </div>
                    ) : (
                        <>
                            <div className="cage-list-container" onClick={() => showFiltersCages && setShowFiltersCages(false)}>
                                {isMobile ? renderMobileCageList() : renderDesktopCageTable()}
                            </div>

                            {!isMobile && totalCagesCount > 0 && (
                                <div className="pagination-controls">
                                    <div>
                                        Items per page:
                                        <select onChange={handleItemsPerPageChangeCages} value={itemsPerPageCages}>
                                            {ITEMS_PER_PAGE_OPTIONS.map(option => (
                                                <option key={option} value={option}>{option}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <button onClick={() => handlePageChangeCages(currentPageCages - 1)} disabled={currentPageCages === 1}>
                                            Previous
                                        </button>
                                        <span>Page {currentPageCages} of {totalPagesCages}</span>
                                        <button onClick={() => handlePageChangeCages(currentPageCages + 1)} disabled={currentPageCages === totalPagesCages}>
                                            Next
                                        </button>
                                    </div>
                                </div>
                            )}
                            {isMobile && isLoadingMore && (
                                <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>Loading more...</div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {createPortal(
                <div className={`slide-out-panel ${showCageFormPanel ? 'slide-out-panel-open' : ''}`}>
                    <CageFormPanel
                        editingId={editingId}
                        formData={formData}
                        onChange={handleChange}
                        onSubmit={handleSubmit}
                        isSubmitting={isSubmitting}
                        onCancel={resetFormAndPanel}
                        error={formError}
                    />
                </div>,
                document.body
            )}

            {showAddLitterModal && (
                <AddLitterModal
                    litterModalData={litterModalData}
                    setLitterModalData={setLitterModalData}
                    previousMales={litterModalPreviousMales}
                    mice={mice}
                    formatMouse={formatMouseForDropdown}
                    onSubmit={handleConfirmAddLitter}
                    isSubmitting={isSubmitting}
                    onCancel={() => setShowAddLitterModal(false)}
                    error={formError}
                />
            )}

            {/* Pregnancy Modal */}
            {showPregnancyModal && (
                <PregnancyModal
                    pregnancyModalData={pregnancyModalData}
                    setPregnancyModalData={setPregnancyModalData}
                    previousMales={pregnancyModalPreviousMales}
                    mice={mice}
                    formatMouse={formatMouseForDropdown}
                    onSubmit={handleConfirmAddPregnancy}
                    isSubmitting={isSubmitting}
                    onCancel={() => setShowPregnancyModal(false)}
                    error={formError}
                />
            )}

            {/* Transfer Modal */}
            {showTransferModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
                    <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%', maxHeight: '90%', overflowY: 'auto' }}>
                        <h3>Transfer Cage</h3>
                        <p>Transfer this cage and all its mice to another lab member. This will archive the cage and mice for you and create new copies for the recipient.</p>

                        <div className="form-group">
                            <label>Transfer to:</label>
                            <select
                                value={transferRecipientId}
                                onChange={(e) => setTransferRecipientId(e.target.value)}
                                style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }}
                            >
                                <option value="">Select lab member...</option>
                                {labMembers.map(member => (
                                    <option key={member.id} value={member.id}>
                                        {member.first_name && member.last_name
                                            ? `${member.first_name} ${member.last_name} (${member.username})`
                                            : member.username}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {formError && <div className="error">{formError}</div>}

                        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                            <button onClick={handleConfirmCageTransfer} style={{ flex: 1 }} className="primary-button" disabled={isTransferringCages}>{isTransferringCages ? 'Transferring...' : 'Confirm Transfer'}</button>
                            <button onClick={() => setShowTransferModal(false)} className="secondary-button" style={{ flex: 1 }}>Cancel</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CageManagement;
